import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry with comprehensive monitoring
 * This includes error tracking, performance monitoring, session replay, and more
 */
export const initializeSentry = () => {
  Sentry.init({
    // DSN for sending events to Sentry
    dsn: 'https://77de7a7df2047db57786418ea611b19e@sentry.elva.ai/13',

    // Environment configuration
    environment: import.meta.env.MODE || 'production',
    
    // Send default PII (Personally Identifiable Information) to Sentry
    // Includes IP addresses, user agents, etc.
    sendDefaultPii: true,

    // Enable performance monitoring
    // Sample rate: 1.0 = 100% of transactions
    // Adjust based on traffic volume (e.g., 0.1 = 10% for high traffic apps)
    tracesSampleRate: import.meta.env.PROD ? 0.5 : 1.0,

    // React Router integration for automatic transaction creation
    integrations: [
      // Browser tracing integration for performance monitoring
      Sentry.browserTracingIntegration(),

      // Session Replay for visual debugging
      // Captures user sessions including DOM interactions
      Sentry.replayIntegration({
        // Mask all text and input content for privacy
        maskAllText: false,
        blockAllMedia: false,
      }),

      // Browser Profiling for detailed performance insights
      Sentry.browserProfilingIntegration(),

      // HTTP Client integration for tracking fetch/XHR requests
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[400, 599]],
      }),
    ],

    // Set sample rate for profiling
    profilesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay sample rates
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0.5,
    replaysOnErrorSampleRate: 1.0,

    // Release tracking for better error context
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // Breadcrumbs configuration
    maxBreadcrumbs: 50,

    // Before sending an event, you can modify it here
    beforeSend(event, hint) {
      // Filter out certain errors if needed
      if (event.exception) {
        const error = hint.originalException;
        
        // Example: Don't send cancelled requests
        if (error instanceof Error && error.message.includes('cancelled')) {
          return null;
        }
      }

      // Add custom context to events
      if (event.request) {
        event.request.headers = {
          ...event.request.headers,
          'User-Agent': navigator.userAgent,
        };
      }

      return event;
    },

    // Before sending a transaction, you can modify it here
    beforeSendTransaction(transaction) {
      // Filter out certain transactions if needed
      // Example: Don't send very fast transactions
      const duration = transaction.timestamp ? 
        (transaction.timestamp - (transaction.start_timestamp || 0)) * 1000 : 0;
      
      if (duration < 10) {
        return null;
      }

      return transaction;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      
      // Random plugins/extensions
      'Can\'t find variable: ZiteReader',
      'jigsaw is not defined',
      'ComboSearch is not defined',
      
      // Facebook errors
      'fb_xd_fragment',
      
      // Network errors that are not actionable
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      
      // ResizeObserver errors (benign)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],

    // Deny URLs - don't report errors from these sources
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      
      // Firefox extensions
      /^resource:\/\//i,
      /^moz-extension:\/\//i,
      
      // Safari extensions
      /^safari-extension:\/\//i,
      
      // Other
      /^webkit-masked-url:\/\//i,
    ],
  });
};

/**
 * Set user context for better error tracking
 */
export const setUserContext = (user: {
  id?: string;
  email?: string;
  username?: string;
  [key: string]: any;
}) => {
  Sentry.setUser(user);
};

/**
 * Clear user context (e.g., on logout)
 */
export const clearUserContext = () => {
  Sentry.setUser(null);
};

/**
 * Set custom context
 */
export const setCustomContext = (key: string, context: Record<string, any>) => {
  Sentry.setContext(key, context);
};

/**
 * Add breadcrumb manually
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Capture exception manually
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.keys(context).forEach((key) => {
        scope.setContext(key, context[key]);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

/**
 * Capture message manually
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info'
) => {
  Sentry.captureMessage(message, level);
};

/**
 * Create a custom transaction for performance monitoring
 */
export const startTransaction = (
  name: string,
  op: string = 'custom'
): Sentry.Span | undefined => {
  return Sentry.startSpan(
    {
      name,
      op,
    },
    () => undefined
  );
};

/**
 * Set tags for better error filtering
 */
export const setTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};

/**
 * Set tags in bulk
 */
export const setTags = (tags: Record<string, string>) => {
  Sentry.setTags(tags);
};

